import { useState, useEffect, useCallback } from 'react';
import { submitWhisper, listMyWhispersForStory, deleteWhisper } from './narratorWhispers.js';

/**
 * "Siren's Box" — per-story whisper interface for guiding the AI.
 *
 * - Story OWNER mode (mode='owner'): leave self-notes for the AI on
 *   your own story. AI weaves them into the next response, then they
 *   flip to ✓ used. Useful for "next beat: have Cruz find the journal"
 *   without having to bake it into a message.
 *
 * - Spectator mode (mode='spectator'): you're watching someone else's
 *   RP. Whispers go to THEIR AI prompt; they never see the text.
 *
 * Each story gets its own box (scoped by story_id everywhere).
 */
export default function SirenWhisperBox({ storyId, fromUserId, toUserId, toUserName, storyName, mode = 'spectator' }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  // Collapsed by default on mobile (it was eating half the composer screen),
  // and the choice persists — reopening the story doesn't re-expand it.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('mfml_sirenbox_collapsed');
      if (saved != null) return saved === '1';
    } catch { /* default below */ }
    return typeof window !== 'undefined' && window.innerWidth < 700;
  });
  const toggleCollapsed = () => {
    setCollapsed(c => {
      try { localStorage.setItem('mfml_sirenbox_collapsed', c ? '0' : '1'); } catch {}
      return !c;
    });
  };
  const [whispers, setWhispers] = useState([]);

  // (No loading state — nothing ever rendered a spinner for the 5s poll, and
  // the dead setState pair was re-rendering the box twice per refresh.)
  const refresh = useCallback(async () => {
    if (!storyId || !fromUserId) return;
    const data = await listMyWhispersForStory(storyId, fromUserId);
    setWhispers(data);
  }, [storyId, fromUserId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Lightweight polling so the spectator sees their whispers flip to
  // "used" right after the player sends and the AI responds.
  useEffect(() => {
    const id = setInterval(refresh, 12_000);
    return () => clearInterval(id);
  }, [refresh]);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const res = await submitWhisper({ storyId, fromUserId, toUserId, text: trimmed });
      if (res?.error) {
        alert(`Couldn't send the whisper: ${res.error.message || 'unknown error'}`);
      } else {
        setText('');
        refresh();
      }
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    await deleteWhisper(id);
    refresh();
  };

  if (!storyId || !fromUserId || !toUserId) return null;

  return (
    <div style={{
      background: 'var(--ds-glass, rgba(29, 16, 19, 0.77))',
      backdropFilter: 'blur(14px) saturate(1.5)',
      WebkitBackdropFilter: 'blur(14px) saturate(1.5)',
      border: '1px solid var(--ds-glass-edge, #c4a8823d)',
      borderRadius: collapsed ? 'var(--ds-r-pill, 999px)' : 'var(--ds-r-lg, 20px)',
      padding: collapsed ? '4px 14px' : '12px 14px',
      margin: collapsed ? '6px 10px' : '10px',
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      color: 'var(--ds-ink-hi, #f1e7e3)',
      boxShadow: 'var(--ds-shadow-1), inset 0 1px 0 var(--ds-glass-light, rgba(255,255,255,0.055))',
    }}>
      <div
        onClick={toggleCollapsed}
        role="button"
        aria-expanded={!collapsed}
        aria-label="Toggle Siren's Box"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', gap: '8px', minHeight: '32px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: '16px' }} aria-hidden="true">🌊</span>
          <span style={{
            fontFamily: "'Cinzel', serif",
            fontSize: '12px',
            letterSpacing: '1.5px',
            color: 'var(--ds-gold, #c8ab80)',
            textTransform: 'uppercase',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}>Siren's Box</span>
          {storyName && (
            <span style={{ fontSize: '11px', color: 'var(--ds-ink-low, #9c8983)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              — for "{storyName}"
            </span>
          )}
        </div>
        <span style={{ fontSize: '11px', color: 'var(--ds-ink-low, #9c8983)', flexShrink: 0 }}>{collapsed ? '▸' : '▾'}</span>
      </div>

      {!collapsed && (
        <>
          <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--ds-ink-low, #9c8983)', lineHeight: 1.4 }}>
            {mode === 'owner' ? (
              <>Drop a hint, plot beat, mood shift, or specific direction for the next AI response. Each note steers exactly one beat — flips to ✓ used after the AI uses it. <span style={{ color: 'var(--ds-accent, #c62828)' }}>Spectators can't see your notes either.</span></>
            ) : (
              <>Drop a hint, plot beat, mood shift, or specific direction. The AI will weave it into the next response. <span style={{ color: 'var(--ds-accent, #c62828)' }}>{toUserName || 'They'} won't see your whisper text — only what comes out of it.</span></>
            )}
          </div>

          <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmit(); } }}
              placeholder={mode === 'owner' ? "e.g. have him notice the bracelet I'm still wearing" : "e.g. have him notice the bracelet she's still wearing"}
              rows={2}
              style={{
                flex: 1,
                background: 'var(--ds-input, #0d0708)',
                color: 'var(--ds-ink-hi, #f1e7e3)',
                border: '1px solid var(--ds-line-soft, #2a161b)',
                borderRadius: 'var(--ds-r-md, 14px)',
                padding: '8px 10px',
                fontSize: '13px',
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                resize: 'vertical',
                minHeight: '44px',
                maxHeight: '120px',
                outline: 'none',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
              }}
            />
            <button
              onClick={handleSubmit}
              disabled={!text.trim() || sending}
              style={{
                padding: '8px 14px',
                background: text.trim() ? 'linear-gradient(135deg, var(--ds-accent-deeper, #8b0000), var(--ds-accent, #c62828))' : 'var(--ds-line-soft, #2a161b)',
                border: text.trim() ? '1px solid var(--ds-gold-a40, #c4a88240)' : 'none',
                borderRadius: 'var(--ds-r-pill, 999px)',
                color: 'var(--ds-ink-hi, #f1e7e3)',
                fontFamily: "'Cinzel', serif",
                fontSize: '11px',
                letterSpacing: '1px',
                cursor: text.trim() ? 'pointer' : 'default',
                opacity: sending ? 0.5 : 1,
                whiteSpace: 'nowrap',
                touchAction: 'manipulation',
                transition: 'all 0.3s ease',
                boxShadow: text.trim() ? 'var(--ds-glow-gold)' : 'none',
              }}
            >
              {sending ? '…' : (mode === 'owner' ? 'Note' : 'Whisper')}
            </button>
          </div>

          {whispers.length > 0 && (
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '10px', color: 'var(--ds-ink-faint, #6e5a54)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>
                {mode === 'owner' ? `Your notes (${whispers.length})` : `Your whispers (${whispers.length})`}
              </div>
              {whispers.map(w => (
                <div
                  key={w.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '6px',
                    padding: '6px 8px',
                    background: w.consumed ? 'var(--ds-surface-2, #1d1013)' : 'var(--ds-accent-a15, #c6282815)',
                    borderLeft: w.consumed ? '2px solid var(--ds-line-soft, #2a161b)' : '2px solid var(--ds-accent, #c62828)',
                    borderRadius: '0 4px 4px 0',
                    fontSize: '12px',
                    opacity: w.consumed ? 0.55 : 1,
                  }}
                >
                  <span style={{ fontSize: '10px', color: w.consumed ? 'var(--ds-ink-low, #9c8983)' : 'var(--ds-accent, #c62828)', flexShrink: 0, paddingTop: '2px' }}>
                    {w.consumed ? '✓ used' : '○ pending'}
                  </span>
                  <span style={{ flex: 1, lineHeight: 1.4, color: 'var(--ds-ink-hi, #f1e7e3)' }}>{w.whisper_text}</span>
                  {!w.consumed && (
                    <button
                      onClick={() => handleDelete(w.id)}
                      title="Withdraw whisper"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--ds-ink-low, #9c8983)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        padding: '0 2px',
                        flexShrink: 0,
                        transition: 'color 0.2s ease',
                      }}
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
